import { Heading, Text } from '@/hen-ui/atomics';
import { type PropsWithChildren, type FC } from 'react';
import { cn } from '@/hen-ui/utils';
import {
  type PageSectionVariantProps,
  pageSectionVariants,
} from './pageSection.variants';

/**
 * Props for the PageSection component
 *
 * PageSection is a composite component that combines atomic components
 * (Heading and Text) to create consistent page sections.
 */
export interface PageSectionProps
  extends PropsWithChildren<PageSectionVariantProps> {
  /** The main heading text for the section (rendered as h2) */
  title: string;
  /** The subtitle/description text displayed below the title (rendered as b3 variant) */
  subtitle: string;
  /** Text alignment for the title and subtitle
   * @default 'center'
   */
  alignment?: 'center' | 'left' | 'right';
  /** Additional CSS classes for the root element */
  className?: string;
}

/**
 * PageSection - A flexible section container component for organizing page content
 *
 * This composite component provides a consistent way to structure page sections with
 * a heading, subtitle, and content area. It is composed entirely of atomic components
 * (Heading and Text) and supports multiple visual variants and text alignment options
 * following the HEN Design System.
 *
 * ## Component Composition
 * - Uses `Heading` atomic component (h2 variant) for the title
 * - Uses `Text` atomic component (b3 variant) for the subtitle
 * - All styling is controlled through design tokens and variant props
 *
 * ## Use Cases
 * - Feature sections on landing pages
 * - Content organization on article pages
 * - Service/product descriptions
 * - Team or company information sections
 * - Call-to-action sections
 *
 * @example
 * ```tsx
 * <PageSection
 *   title="Our Mission"
 *   subtitle="Empowering students to achieve their educational goals"
 *   variant="light"
 *   alignment="center"
 * >
 *   <div>Section content goes here</div>
 * </PageSection>
 * ```
 *
 * @param title - The main heading text for the section (rendered as h2)
 * @param subtitle - The subtitle/description text displayed below the title (rendered as b3)
 * @param variant - Visual style variant ('light' | 'white-outline')
 * @param alignment - Text alignment for title and subtitle ('center' | 'left' | 'right')
 * @param children - Content to display within the section (any React node)
 */
export const PageSection: FC<PageSectionProps> = ({
  children,
  title,
  subtitle,
  variant = 'light',
  alignment = 'center',
  className,
}) => {
  return (
    <div className={cn(pageSectionVariants({ variant, alignment }), className)}>
      <div className='flex flex-col gap-xxs'>
        <Heading variant='h2'>{title}</Heading>
        <Text className='text-subtext' variant='b3'>
          {subtitle}
        </Text>
      </div>
      {children}
    </div>
  );
};

PageSection.displayName = 'PageSection';
